import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { 
    Typography, 
    Button, 
    TextField, 
    InputAdornment,
    Tooltip,
    Avatar
} from "@material-ui/core";
import "./UnifiedLineupTable.css";

import { ProfileType, LineupStatusType } from "../../../types/enum";

@inject("lineupStore", "bookingRequestStore", "profileStore", "templateStore")
@observer
class UnifiedLineupTable extends Component {
    state = {
        expandedRowIndex: null
    };

    handleExpandRow = (index) => {
        this.setState(prevState => ({
            expandedRowIndex: prevState.expandedRowIndex === index ? null : index
        }));
    };

    handleCollapseRow = () => {
        this.setState({ expandedRowIndex: null });
    };

    isActOwnerProfileId = profileId => {
        const { actAdminId } = this.props;

        return (
            profileId !== undefined &&
            profileId !== null &&
            actAdminId !== undefined &&
            actAdminId !== null &&
            String(profileId) === String(actAdminId)
        );
    };

    getAvailablePlayersForInstrument = (lineup, allLineups) => {
        const players = [];
        
        if (!lineup.actRoster || !lineup.actRoster.rosters) {
            return players;
        }
        
        const rosters = lineup.actRoster.rosters;
        for (let i = 0; i < rosters.length; i++) {
            const p = rosters[i];
            if (p && p.profile) {
                if (this.props.isPayByActOwner && this.isActOwnerProfileId(p.profile.id)) {
                    continue;
                }

                const isAlreadyAssigned = allLineups && allLineups.some(l => 
                    l.profileId === p.profile.id && l.profileId !== lineup.profileId
                );
                
                const isCurrentPlayer = p.profile.id === lineup.profileId;
                
                if (!isCurrentPlayer) {
                    players.push({
                        id: p.profile.id,
                        profileId: p.profile.id,
                        firstName: p.profile.firstName || "",
                        lastName: p.profile.lastName || "",
                        available: p.available !== false && !isAlreadyAssigned,
                        profileImage: p.profile.profileImage,
                        isAlreadyAssigned: isAlreadyAssigned,
                        fullProfile: p.profile
                    });
                }
            }
        }
        
        return players;
    };

    getInitials = (firstName, lastName) => {
        const first = firstName ? firstName.charAt(0).toUpperCase() : "";
        const last = lastName ? lastName.charAt(0).toUpperCase() : "";
        return first + last;
    };

    // Get display status for a lineup entry
    // Matches the original statusText logic from BookingLineup.js
    getDisplayStatus = (lineup) => {
        const { actAdminId } = this.props;
        const { currentProfile } = this.props.profileStore;
        const status = lineup.status;
        const isInvited = lineup.id ? true : false;
        
        // Act owner always shows as Accepted
        if (this.isActOwnerProfileId(lineup.profileId)) {
            return { label: "Accepted", className: "status-accepted" };
        }
        
        switch (status) {
            case LineupStatusType.New.ordinal: // 0
                if (isInvited) {
                    return { label: "Invited", className: "status-invited" };
                } else if (currentProfile?.id === lineup.profileId) {
                    return { label: "Accepted", className: "status-accepted" };
                } else {
                    return { label: "Not Yet Invited", className: "status-not-invited" };
                }
            case LineupStatusType.Declined.ordinal: // 1
                return { label: "Declined", className: "status-declined" };
            case LineupStatusType.Hold.ordinal: // 2
                return { label: "Hold", className: "status-hold" };
            case LineupStatusType.Accepted.ordinal: // 3
                return { label: "Accepted", className: "status-accepted" };
            case LineupStatusType.Confirmed.ordinal: // 4
                return { label: "Confirmed", className: "status-confirmed" };
            case LineupStatusType.Cancelled.ordinal: // 5
            case LineupStatusType.Removed.ordinal: // 6
                return { label: "Cancelled", className: "status-cancelled" };
            default:
                if (isInvited) {
                    return { label: "Invited", className: "status-invited" };
                } else {
                    return { label: "Not Yet Invited", className: "status-not-invited" };
                }
        }
    };

    // Keep these for backward compatibility but they use the new logic
    getStatusClass = (lineup) => {
        return this.getDisplayStatus(lineup).className;
    };

    getStatusLabel = (lineup) => {
        return this.getDisplayStatus(lineup).label;
    };

    formatCurrency = (value) => {
        const { currentProfile } = this.props.profileStore;
        const symbol = currentProfile?.currencySymbol || "$";
        const num = parseFloat(value) || 0;
        return `${symbol} ${num.toFixed(2)}`;
    };

    render() {
        const {
            lineups,
            filteredLineupsWithIndex,
            isAdmin,
            isPayByActOwner,
            isSoloAdminLineup,
            canSave,
            actFee,
            currencySymbol,
            actOwnerBalance,
            totalPlayerFees,
            totalSuper,
            totalInvoice,
            isOverBudget,
            isSaveDisabled,
            onEditFee,
            onDoneEditFee,
            onCancelEditFee,
            onFeeChange,
            onChangePlayer,
            onRemovePlayer,
            onAddPerformer,
            onSave,
            getPlayerFee,
            getPlayerSuper,
            getPlayerInvoice,
            actAdminId,
            showProductionCrew,
            performanceFee,
            isPlayerView,
            currentProfileId
        } = this.props;

        const { expandedRowIndex } = this.state;

        let playerLineupsWithIndex = isPayByActOwner
            ? filteredLineupsWithIndex.filter(({ lineup }) => !this.isActOwnerProfileId(lineup.profileId))
            : filteredLineupsWithIndex;


        const productionCrewLineups = lineups
            ? lineups.filter(x => x.type === 1)
            : [];

        return (
            <div className="unified-lineup-container">
                <div className="unified-lineup-section-header">
                    <Typography variant="h6" className="unified-lineup-section-title">
                        {isPayByActOwner ? "Line-up & Fee Allocation" : "Players Line Up"}
                    </Typography>
                    <Typography variant="body2" className="unified-lineup-section-subtitle">
                        {isPayByActOwner 
                            ? "This booking uses the 'Act Player Invoices paid by Hirer (agency/venue)' setting. You must update the player fees below and hit Save & Invite in order for invoices to generate."
                            : "Select and invite your players to this gig."
                        }
                    </Typography>
                </div>

                {isSoloAdminLineup ? (
                    <div className="unified-lineup-table-container">
                        <div className="unified-lineup-gig-fee-bar">
                            <span className="gig-fee-label">Total Gig Fee</span>
                            <span className="gig-fee-value">{this.formatCurrency(performanceFee)}</span>
                        </div>
                        <div style={{ padding: "24px 16px", textAlign: "center", color: "#888" }}>
                            <Typography variant="body2">
                                You are the only performer in this lineup. Add players to distribute fees.
                            </Typography>
                            {isAdmin && (
                                <div className="add-performer-btn" onClick={onAddPerformer} style={{ display: "inline-flex", marginTop: 12 }}>
                                    <span className="add-icon">+</span>
                                    <span>Add New Performer</span>
                                </div>
                            )}
                        </div>
                        {canSave && (
                            <div className="unified-lineup-save-container">
                                <Button
                                    variant="contained"
                                    className="btn-save"
                                    disabled={isSaveDisabled}
                                    onClick={onSave}
                                >
                                    Save & Invite Players
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                <div className="unified-lineup-table-container">
                    {!isPlayerView && (
                        <div className="unified-lineup-gig-fee-bar">
                            <span className="gig-fee-label">Total Gig Fee</span>
                            <span className="gig-fee-value">{this.formatCurrency(performanceFee)}</span>
                        </div>
                    )}

                    <div className="unified-lineup-table-header">
                        <span className="table-header-title">Players & Fee Breakdown</span>
                    </div>

                    {/* Mobile Card Layout */}
                    <div className="mobile-lineup-cards">
                        {playerLineupsWithIndex.map(({ lineup, originalIndex }) => {
                            const isActOwner = this.isActOwnerProfileId(lineup.profileId);
                            const isEditing = lineup.editMode;
                            const playerFee = getPlayerFee(lineup);
                            const playerSuper = getPlayerSuper(lineup);
                            const playerInvoice = getPlayerInvoice(lineup);
                            
                            return (
                                <div key={lineup.profileId || originalIndex} className="mobile-player-card">
                                    <div className="mobile-player-main">
                                        <Avatar className="mobile-player-avatar">
                                            {this.getInitials(lineup.profile?.firstName, lineup.profile?.lastName)}
                                        </Avatar>
                                        <div className="mobile-player-info">
                                            <div className="mobile-player-name">
                                                {lineup.profile ? `${lineup.profile.firstName} ${lineup.profile.lastName}` : "Unknown"}
                                            </div>
                                            {isActOwner && <div className="mobile-player-role">Act Owner</div>}
                                            <div className="mobile-player-instrument">{lineup.name || "-"}</div>
                                            <span className={`mobile-status-chip ${this.getStatusClass(lineup)}`}>
                                                {this.getStatusLabel(lineup)}
                                            </span>
                                        </div>
                                    </div>

                                    {!isPlayerView && (
                                    <div className="mobile-fee-summary">
                                        <div className="mobile-fee-item">
                                            <div className="mobile-fee-label">Player Fee</div>
                                            {isEditing ? (
                                                <div className="mobile-fee-input-container">
                                                    <TextField
                                                        value={lineup.manualFee !== undefined && lineup.manualFee !== null ? lineup.manualFee : lineup.fee}
                                                        onChange={(e) => onFeeChange(e.target.value, originalIndex)}
                                                        type="text"
                                                        className="mobile-fee-input"
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    {currencySymbol || "$"}
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="mobile-fee-value">{this.formatCurrency(playerFee)}</div>
                                            )}
                                        </div>
                                        <div className="mobile-fee-item">
                                            <div className="mobile-fee-label">Super</div>
                                            <div className="mobile-fee-value">{playerSuper ? this.formatCurrency(playerSuper) : "-"}</div>
                                        </div>
                                        <div className="mobile-fee-item">
                                            <div className="mobile-fee-label">Invoice</div>
                                            <div className="mobile-fee-value">{this.formatCurrency(playerInvoice)}</div>
                                        </div>
                                    </div>
                                    )}

                                    {isAdmin && (
                                        <div className="mobile-player-actions">
                                            {isEditing ? (
                                                <>
                                                    <Button 
                                                        variant="contained" 
                                                        className="mobile-btn mobile-btn-done"
                                                        onClick={() => onDoneEditFee(originalIndex)}
                                                    >
                                                        Done
                                                    </Button>
                                                    <Button 
                                                        variant="outlined" 
                                                        className="mobile-btn mobile-btn-cancel"
                                                        onClick={() => onCancelEditFee(originalIndex)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button 
                                                        variant="contained" 
                                                        className="mobile-btn mobile-btn-primary"
                                                        onClick={() => onEditFee(originalIndex)}
                                                    >
                                                        Edit Fee
                                                    </Button>
                                                    {lineup.actRoster && lineup.actRoster.rosters ? (
                                                        <Button 
                                                            variant="outlined" 
                                                            className={`mobile-btn mobile-btn-secondary ${expandedRowIndex === originalIndex ? "active" : ""}`}
                                                            onClick={() => this.handleExpandRow(originalIndex)}
                                                        >
                                                            Change
                                                        </Button>
                                                    ) : (
                                                        <span className="no-roster-text">No roster</span>
                                                    )}
                                                    <Button 
                                                        variant="outlined" 
                                                        className="mobile-btn mobile-btn-danger"
                                                        onClick={() => onRemovePlayer(lineup)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    
                                    {/* Mobile Expandable Player Selection */}
                                    {expandedRowIndex === originalIndex && lineup.actRoster && lineup.actRoster.rosters && (
                                        <div className="mobile-expanded-player-section">
                                            <div className="expanded-player-header">
                                                <div className="expanded-player-title">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                                        <circle cx="12" cy="12" r="10"/>
                                                        <path d="M8 12h8M12 8v8"/>
                                                    </svg>
                                                    Select a {lineup.name || "Player"}
                                                </div>
                                                <div className="expanded-player-close" onClick={this.handleCollapseRow}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M18 6L6 18M6 6l12 12"/>
                                                    </svg>
                                                    Close
                                                </div>
                                            </div>
                                            <div className="expanded-player-list">
                                                {this.getAvailablePlayersForInstrument(lineup, lineups).map((player, playerIdx) => (
                                                    <div 
                                                        key={player.id || playerIdx} 
                                                        className={`expanded-player-item ${!player.available ? "unavailable" : ""}`}
                                                    >
                                                        <div className="expanded-player-info">
                                                            <Avatar className="expanded-player-avatar">
                                                                {this.getInitials(player.firstName, player.lastName)}
                                                            </Avatar>
                                                            <div className="expanded-player-details">
                                                                <span className="expanded-player-name">
                                                                    {player.firstName} {player.lastName}
                                                                </span>
                                                                <span className={`expanded-player-status ${player.available ? "available" : "unavailable"}`}>
                                                                    {player.available ? "Available" : (player.isAlreadyAssigned ? "Already in lineup" : "Unavailable")}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            variant="contained"
                                                            className={`btn-select-player ${!player.available ? "disabled" : ""}`}
                                                            disabled={!player.available}
                                                            onClick={() => {
                                                                onChangePlayer(originalIndex, player);
                                                                this.handleCollapseRow();
                                                            }}
                                                        >
                                                            Select
                                                        </Button>
                                                    </div>
                                                ))}
                                                {this.getAvailablePlayersForInstrument(lineup, lineups).length === 0 && (
                                                    <div className="expanded-player-empty">
                                                        No other players in this instrument roster
                                                    </div>
                                                )}
                                                <div className="expanded-player-invite" onClick={() => { this.handleCollapseRow(); onAddPerformer(); }}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="12" cy="12" r="10"/>
                                                        <path d="M12 8v8M8 12h8"/>
                                                    </svg>
                                                    Invite a new player
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {isAdmin && (
                            <div className="mobile-add-performer" onClick={onAddPerformer}>
                                <span className="mobile-add-icon">+</span>
                                <span>Add New Performer</span>
                            </div>
                        )}

                        {/* Mobile Totals Section */}
                        {!isPlayerView && <div className="mobile-totals-section">
                            {isPayByActOwner && (
                                <div className={`mobile-totals-row mobile-balance-row ${isOverBudget ? "negative" : actOwnerBalance === 0 ? "zero" : "positive"}`}>
                                    <div className="mobile-totals-label">Act Owner Balance</div>
                                    <div className="mobile-totals-grid">
                                        <div className="mobile-totals-cell">
                                            <div className="mobile-cell-label">Player Fee</div>
                                            <div className={`mobile-cell-value ${isOverBudget ? "negative" : actOwnerBalance > 0 ? "positive" : ""}`}>
                                                {isOverBudget ? "-" : "+"} {this.formatCurrency(actOwnerBalance)}
                                            </div>
                                        </div>
                                        <div className="mobile-totals-cell">
                                            <div className="mobile-cell-label">Super</div>
                                            <div className="mobile-cell-value muted">-</div>
                                        </div>
                                        <div className="mobile-totals-cell">
                                            <div className="mobile-cell-label">Invoice</div>
                                            <div className="mobile-cell-value muted">-</div>
                                        </div>
                                    </div>
                                    <div className="mobile-totals-note">
                                        {isOverBudget ? "Over budget!" : "Gig Fee - Player Fees"}
                                    </div>
                                </div>
                            )}
                            <div className="mobile-totals-row mobile-total-row">
                                <div className="mobile-totals-label">Total</div>
                                <div className="mobile-totals-grid">
                                    <div className="mobile-totals-cell">
                                        <div className="mobile-cell-label">Player Fee</div>
                                        <div className="mobile-cell-value">{this.formatCurrency(totalPlayerFees)}</div>
                                    </div>
                                    <div className="mobile-totals-cell">
                                        <div className="mobile-cell-label">Super</div>
                                        <div className="mobile-cell-value">{this.formatCurrency(totalSuper)}</div>
                                    </div>
                                    <div className="mobile-totals-cell">
                                        <div className="mobile-cell-label">Invoice</div>
                                        <div className="mobile-cell-value">{this.formatCurrency(totalInvoice)}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        }
                    </div>

                    {/* Desktop Table Layout */}
                    <table className="unified-lineup-table">
                        <thead>
                            <tr>
                                <th style={{ width: isPlayerView ? "30%" : "22%" }}>Player</th>
                                <th className="instrument-col" style={{ width: "12%" }}>Instrument</th>
                                <th className="status-col" style={{ width: "10%" }}>Status</th>
                                {!isPlayerView && <th style={{ width: "10%" }}>Player Fee</th>}
                                {!isPlayerView && <th className="super-col" style={{ width: "8%" }}>Super</th>}
                                {!isPlayerView && <th className="invoice-col" style={{ width: "10%" }}>Invoice</th>}
                                <th style={{ width: "28%" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {playerLineupsWithIndex.map(({ lineup, originalIndex }) => {
                                const isActOwner = this.isActOwnerProfileId(lineup.profileId);
                                const isEditing = lineup.editMode;
                                const playerFee = getPlayerFee(lineup);
                                const playerSuper = getPlayerSuper(lineup);
                                const playerInvoice = getPlayerInvoice(lineup);
                                
                                return (
                                    <Fragment key={lineup.profileId || originalIndex}>
                                    <tr className={`${isEditing ? "edit-mode-row" : ""} ${expandedRowIndex === originalIndex ? "expanded-active-row" : ""}`}>
                                        <td>
                                            <div className="player-cell">
                                                <Avatar className="player-avatar">
                                                    {this.getInitials(lineup.profile?.firstName, lineup.profile?.lastName)}
                                                </Avatar>
                                                <div className="player-info">
                                                    <span className="player-name">
                                                        {lineup.profile ? `${lineup.profile.firstName} ${lineup.profile.lastName}` : "Unknown"}
                                                    </span>
                                                    {isActOwner && <span className="player-role">Act Owner</span>}
                                                    <span className="player-instrument mobile-only">{lineup.name || "-"}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="instrument-col">
                                            <span className="instrument-badge">{lineup.name || "-"}</span>
                                        </td>
                                        <td className="status-col">
                                            <span className={`status-chip ${this.getStatusClass(lineup)}`}>
                                                {this.getStatusLabel(lineup)}
                                            </span>
                                        </td>
                                        {!isPlayerView && (
                                        <td>
                                            {isEditing ? (
                                                <div className="fee-input-container">
                                                    <TextField
                                                        value={lineup.manualFee !== undefined && lineup.manualFee !== null ? lineup.manualFee : lineup.fee}
                                                        onChange={(e) => onFeeChange(e.target.value, originalIndex)}
                                                        type="text"
                                                        className="fee-input"
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    {currencySymbol || "$"}
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <span className="fee-display">{this.formatCurrency(playerFee)}</span>
                                            )}
                                        </td>
                                        )}
                                        {!isPlayerView && <td>{playerSuper ? this.formatCurrency(playerSuper) : "-"}</td>}
                                        {!isPlayerView && <td>{this.formatCurrency(playerInvoice)}</td>}
                                        <td>
                                            {isEditing ? (
                                                <div className="edit-mode-buttons">
                                                    <Button 
                                                        variant="contained" 
                                                        className="btn-done"
                                                        onClick={() => onDoneEditFee(originalIndex)}
                                                    >
                                                        Done
                                                    </Button>
                                                    <Button 
                                                        variant="outlined" 
                                                        className="btn-cancel"
                                                        onClick={() => onCancelEditFee(originalIndex)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="action-buttons">
                                                    {isAdmin && (
                                                        <>
                                                            <Button 
                                                                variant="contained" 
                                                                className="btn-edit-fee"
                                                                onClick={() => onEditFee(originalIndex)}
                                                            >
                                                                Edit Fee
                                                            </Button>
                                                            {lineup.actRoster && lineup.actRoster.rosters ? (
                                                                <Button 
                                                                    variant="outlined" 
                                                                    className={`btn-change-player ${expandedRowIndex === originalIndex ? "active" : ""}`}
                                                                    onClick={() => this.handleExpandRow(originalIndex)}
                                                                >
                                                                    Change
                                                                </Button>
                                                            ) : (
                                                                <span className="no-roster-text">No roster</span>
                                                            )}
                                                            <button 
                                                                className="btn-icon-remove" 
                                                                title="Remove"
                                                                onClick={() => onRemovePlayer(lineup)}
                                                            >
                                                                🗑
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                    {/* Expandable Row for Player Selection */}
                                    {expandedRowIndex === originalIndex && lineup.actRoster && lineup.actRoster.rosters && (
                                        <tr className="expanded-player-row">
                                            <td colSpan="7">
                                                <div className="expanded-player-content">
                                                    <div className="expanded-player-header">
                                                        <div className="expanded-player-title">
                                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                                                <circle cx="12" cy="12" r="10"/>
                                                                <path d="M8 12h8M12 8v8"/>
                                                            </svg>
                                                            Select a {lineup.name || "Player"} from your roster
                                                        </div>
                                                        <div className="expanded-player-close" onClick={this.handleCollapseRow}>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M18 6L6 18M6 6l12 12"/>
                                                            </svg>
                                                            Close
                                                        </div>
                                                    </div>
                                                    <div className="expanded-player-list">
                                                        {this.getAvailablePlayersForInstrument(lineup, lineups).map((player, playerIdx) => (
                                                            <div 
                                                                key={player.id || playerIdx} 
                                                                className={`expanded-player-item ${!player.available ? "unavailable" : ""}`}
                                                            >
                                                                <div className="expanded-player-info">
                                                                    <Avatar className="expanded-player-avatar">
                                                                        {this.getInitials(player.firstName, player.lastName)}
                                                                    </Avatar>
                                                                    <div className="expanded-player-details">
                                                                        <span className="expanded-player-name">
                                                                            {player.firstName} {player.lastName}
                                                                        </span>
                                                                        <span className={`expanded-player-status ${player.available ? "available" : "unavailable"}`}>
                                                                            {player.available ? "Available" : (player.isAlreadyAssigned ? "Already in lineup" : "Unavailable")}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <Button
                                                                    variant="contained"
                                                                    className={`btn-select-player ${!player.available ? "disabled" : ""}`}
                                                                    disabled={!player.available}
                                                                    onClick={() => {
                                                                        onChangePlayer(originalIndex, player);
                                                                        this.handleCollapseRow();
                                                                    }}
                                                                >
                                                                    Select
                                                                </Button>
                                                            </div>
                                                        ))}
                                                        {this.getAvailablePlayersForInstrument(lineup, lineups).length === 0 && (
                                                            <div className="expanded-player-empty">
                                                                No other players in this instrument roster
                                                            </div>
                                                        )}
                                                        <div className="expanded-player-invite" onClick={() => { this.handleCollapseRow(); onAddPerformer(); }}>
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <circle cx="12" cy="12" r="10"/>
                                                                <path d="M12 8v8M8 12h8"/>
                                                            </svg>
                                                            Invite a new player to your roster
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                                );
                            })}

                            {isAdmin && (
                                <tr className="add-performer-row">
                                    <td colSpan="7">
                                        <div className="add-performer-btn" onClick={onAddPerformer}>
                                            <span className="add-icon">+</span>
                                            <span>Add New Performer</span>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        {!isPlayerView && (
                        <tfoot>
                            {isPayByActOwner && (
                                <tr className={`footer-row balance-row ${isOverBudget ? "negative" : actOwnerBalance === 0 ? "zero" : "positive"}`}>
                                    <td colSpan="3" style={{ textAlign: "right" }}>Act Owner Balance</td>
                                    <td>
                                        <span className={`balance-value ${isOverBudget ? "negative" : actOwnerBalance === 0 ? "zero" : "positive"}`}>
                                            {isOverBudget ? "-" : ""}{this.formatCurrency(actOwnerBalance)}
                                        </span>
                                    </td>
                                    <td className="super-col">-</td>
                                    <td className="invoice-col">-</td>
                                    <td style={{ fontSize: "12px", color: isOverBudget ? "#c62828" : "#888" }}>
                                        {isOverBudget ? "Over budget!" : "Gig Fee - Player Fees"}
                                    </td>
                                </tr>
                            )}
                            <tr className="footer-row total-row">
                                <td colSpan="3" style={{ textAlign: "right" }}>Total</td>
                                <td>{this.formatCurrency(totalPlayerFees)}</td>
                                <td className="super-col">{this.formatCurrency(totalSuper)}</td>
                                <td className="invoice-col">{this.formatCurrency(totalInvoice)}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                        )}
                    </table>

                {!isPlayerView && isOverBudget && (
                    <div className="unified-lineup-warning">
                        <span className="warning-icon">⚠️</span>
                        <span className="warning-text">
                            <strong>Over Budget:</strong> Player fees ({this.formatCurrency(totalPlayerFees)}) exceed gig fee ({this.formatCurrency(actFee)}). Please reduce player fees to save.
                        </span>
                    </div>
                )}

                {canSave && (
                    <div className="unified-lineup-save-container">
                        <Tooltip
                            title={isOverBudget ? "Roster's Fees is greater than Gig Fee" : ""}
                            disableFocusListener={!isSaveDisabled}
                            disableTouchListener={!isSaveDisabled}
                            disableHoverListener={!isSaveDisabled}
                            placement="top"
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    className="btn-save"
                                    disabled={isSaveDisabled}
                                    onClick={onSave}
                                >
                                    Save & Invite Players
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                )}
                </div>
                )}
                
                {showProductionCrew && productionCrewLineups.length > 0 && (
                    <div className="unified-lineup-production-crew">
                        <div className="unified-lineup-section-header" style={{ marginTop: 24 }}>
                            <Typography variant="h6" className="unified-lineup-section-title">
                                Production Crew
                            </Typography>
                        </div>
                        <div className="unified-lineup-table-container">
                            <table className="unified-lineup-desktop-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: "30%" }}>Role</th>
                                        <th style={{ width: "70%" }}>Name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {productionCrewLineups.map((crew, idx) => (
                                        <tr key={crew.profileId || idx}>
                                            <td>{crew.name || "-"}</td>
                                            <td>{crew.profile ? `${crew.profile.firstName} ${crew.profile.lastName}` : "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default UnifiedLineupTable;
